// apps/client/src/net/handlers/fishing.ts (M1-T15) — the 'fishing' layer follows your personal yields (shadows, shells), and other
// residents' casts and catches show on their avatar and bobber. Your own cast is drawn by the local FishingLoop.
import { getItem, itemsOfKind, type FishEntry } from '@whiskerton/content';
import { groundAt, tierAt } from '@whiskerton/shared/map/codec';
import type { ResidentId, Vec2 } from '@whiskerton/shared';
import { buildFish } from '../../assets/builders/fish';
import { ANIMS } from '../../characters/anims';
import type { Game, NetHandlers } from '../../game/game';
import { useGame } from '../../state/store';
import { SEA_LEVEL, waterSurface } from '../../world/terrain/field';
import { createFishingLayer, fishingLayerOf, registerFishingLayer, ROD_LEAN } from '../../world/fishing';

/** The water surface under a point: the sea level, or a river/pond on its tier (the same heights world/terrain/water.ts draws). */
function waterAt(game: Game): (p: Vec2) => number {
  return (p) => {
    const m = game.map;
    if (!m) return SEA_LEVEL;
    const x = Math.floor(p.x), y = Math.floor(p.y), g = groundAt(m, x, y);
    return g === 'river' || g === 'pond' ? waterSurface(tierAt(m, x, y)) : SEA_LEVEL;
  };
}
function layerFor(game: Game) {
  let l = fishingLayerOf(game);
  const out = game.scenes.out;
  if (!l && out) {
    l = createFishingLayer(out.layer('fishing'), { assets: game.assets, heightAt: (p) => out.heightAt(p), waterAt: game.map ? waterAt(game) : undefined });
    registerFishingLayer(game, l);
  }
  return l;
}
const isMe = (r: string) => useGame.getState().you?.resident === r;
/** Where each other resident's line is in the water, so a miss splashes there and a leaver's bobber can be cleared. */
const casts = new WeakMap<object, Map<ResidentId, Vec2>>();
const castsOf = (game: object) => { let m = casts.get(game); if (!m) { m = new Map(); casts.set(game, m); } return m; };
/** A resident who leaves the outdoors (or the town) mid-cast gets no fish.result, so their bobber goes with them. */
function reelIn(game: Game, r: ResidentId): void {
  if (!castsOf(game).delete(r)) return;
  game.avatarOf(r)?.leanHeld(0);
  fishingLayerOf(game)?.clearBobber(r);
}
const ROD = itemsOfKind('tool').find((t) => t.tool === 'rod')?.id ?? null;
const HOLD_UP_MS = (ANIMS.holdUp.length as number) * 1000;

export const handlers: NetHandlers = {
  onSpace(space, game) {
    if (space.kind !== 'out') return;
    for (const r of [...castsOf(game).keys()]) reelIn(game, r);          // casts seen before we left may have ended unseen
    const l = layerFor(game), you = useGame.getState().you;
    if (l && you) { l.setShadows(you.yields.fish.shadows); void l.setShells(you.yields.shells); }
  },
  onPatch(patch, game) {
    const l = fishingLayerOf(game);
    if (patch.yields?.fish) l?.setShadows(patch.yields.fish.shadows);
    if (patch.yields?.shells) void l?.setShells(patch.yields.shells);
  },
  events: {
    'fish.cast': (e, game) => {
      if (isMe(e.r)) return;                                               // the local FishingLoop already shows it
      const caster = game.avatarOf(e.r);
      caster?.play('cast');
      caster?.leanHeld(ROD_LEAN);
      castsOf(game).set(e.r, e.at);
      layerFor(game)?.showBobber(e.r, e.at, (out) => game.avatarOf(e.r)?.heldTip(out) ?? false);   // the line from their rod
    },
    'fish.result': (e, game) => {
      if (isMe(e.r)) return;
      const l = fishingLayerOf(game), at = castsOf(game).get(e.r);
      castsOf(game).delete(e.r);
      l?.clearBobber(e.r);
      const a = game.avatarOf(e.r);
      a?.leanHeld(0);
      if (!e.caught) { a?.play('reel'); if (at) l?.splash(at); game.sound.sfx('splash'); return; }
      if (!a) return;
      const entry = getItem(e.caught);
      if (entry.kind !== 'fish') return;
      a.play('holdUp');
      const fish = buildFish((entry as FishEntry).look, e.size ?? undefined, e.caught);
      a.hold(fish, 'both');
      setTimeout(() => {                                                   // the rod goes back in their hand afterwards
        if (!ROD || game.avatarOf(e.r) !== a) return;
        void game.assets.model(ROD).then((m) => { if (fish.parent) a.hold(m, 'right'); });   // unless something else took the hand
      }, HOLD_UP_MS);
    },
    'space.leave': (e, game) => { if (!isMe(e.r)) reelIn(game, e.r); },
    'presence.leave': (e, game) => { reelIn(game, e.r); },
  },
};
